/**
 * Client-side rows of the calendar grid model that buildWeeks() produces.
 * A row without hours exists only on the client until a cell is written, same
 * as addRow(): buildWeeks() rebuilds from the server and drops it. Grouping
 * mirrors buildWeeks(): one group per project, one row per phase + description,
 * descriptions compared trimmed and case-insensitively.
 */
type Row = { description: string | null, phase: string, Phase_id: number, user_to_phase_id: number };

const key = (d: string | null) => (d ?? '').trim().toUpperCase();

/** Add one empty row under its project group unless that phase + description is already there. */
export function mergeRow(weeks: any[], utp: any, row: Row): boolean {
  let project = weeks.find(p => p.user_to_phase?.phase?.Project_id === utp?.phase?.Project_id);
  if (!project) {
    project = { user_to_phase: utp, total: 0, wtotal: [], tracks: [], open: true };
    weeks.push(project);
  }
  if (project.tracks.some(t => t.Phase_id === row.Phase_id && key(t.description) === key(row.description))) {
    return false;
  }
  project.tracks.push({ description: row.description, phase: row.phase, Phase_id: row.Phase_id,
    user_to_phase_id: row.user_to_phase_id, total: 0, tracks: {} });
  project.open = true;
  return true;
}

/** Merge another week's entries in as empty rows (#1228). Returns how many rows were added. */
export function mergeWeekRows(weeks: any[], trackings: any[]): number {
  let added = 0;
  for (const o of trackings || []) {
    const utp = o.user_to_phase;
    if (!utp || !(o.duration > 0)) { continue; }
    added += +mergeRow(weeks, utp, { description: o.description ?? null, phase: utp.phase?.name,
      Phase_id: utp.Phase_id, user_to_phase_id: utp.id });
  }
  return added;
}

/** The rows a rebuild would lose, paired with their group's assignment so mergeRow can put them back. */
export function emptyRows(weeks: any[]): { utp: any, row: Row }[] {
  return weeks.flatMap(p => p.tracks.filter(t => !(t.total > 0)).map(t => ({ utp: p.user_to_phase, row: t })));
}
